import { Shield, KeyRound, ArrowRight, AlertCircle, Loader2, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

/**
 * 2FA Verification Component
 * Used during login when user has 2FA enabled
 */
export function TwoFactorVerify({
  twoFactorCode,
  setTwoFactorCode,
  available2FAMethods,
  maskedPhone,
  smsCodeSent,
  sendingSms,
  loading,
  error,
  onSubmit,
  onSendSmsOtp,
  onReset
}) {
  return (
    <>
      {/* Logo and title */}
      <div className="text-center mb-8">
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center shadow-lg bg-gradient-to-br from-emerald-600 to-teal-600">
            <Shield className="w-8 h-8 text-white" />
          </div>
        </div>
        <h1 className="text-3xl font-bold text-slate-900 mb-2">
          Verifiser identitet
        </h1>
        <p className="text-slate-600">
          Skriv inn koden fra autentiseringsappen din
        </p>
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start space-x-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <form onSubmit={onSubmit} className="space-y-6">
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-4">
          <div className="flex items-center space-x-2 text-emerald-700">
            <Shield className="w-5 h-5" />
            <span className="font-medium">To-faktor autentisering</span>
          </div>
          <p className="text-sm text-emerald-600 mt-1">
            Kontoen din er beskyttet. 
            {available2FAMethods.includes('authenticator') && ' Skriv inn koden fra appen din'}
            {available2FAMethods.includes('authenticator') && available2FAMethods.includes('sms') && ' eller'}
            {available2FAMethods.includes('sms') && ' bruk SMS-verifisering'}
            {available2FAMethods.includes('authenticator') && '. Du kan også bruke en backup-kode.'}
          </p>
        </div>

        {/* SMS option */}
        {available2FAMethods.includes('sms') && maskedPhone && (
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-700">SMS til {maskedPhone}</p>
                <p className="text-xs text-slate-500">
                  {smsCodeSent ? 'Kode sendt! Sjekk telefonen din.' : 'Få engangskode på SMS'}
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onSendSmsOtp}
                disabled={sendingSms || smsCodeSent}
                className="border-teal-500 text-teal-600 hover:bg-teal-50"
              >
                {sendingSms ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : smsCodeSent ? (
                  <>
                    <Check className="w-4 h-4 mr-1" />
                    Sendt
                  </>
                ) : (
                  'Send kode'
                )}
              </Button>
            </div>
          </div>
        )}

        <div>
          <Label className="text-sm font-semibold text-slate-700 mb-2 block">Verifiseringskode</Label>
          <div className="relative">
            <KeyRound className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="text"
              value={twoFactorCode}
              onChange={(e) => setTwoFactorCode(e.target.value)}
              placeholder="000 000"
              className="pl-12 h-14 text-center text-2xl tracking-widest rounded-xl border-2 border-slate-200 focus:border-teal-500 transition-colors"
              disabled={loading}
              maxLength={10}
              autoFocus
            />
          </div>
          <p className="text-xs text-slate-500 mt-2">
            Skriv inn den 6-sifrede koden fra 
            {available2FAMethods.includes('authenticator') && ' autentiseringsappen'}
            {available2FAMethods.includes('authenticator') && available2FAMethods.includes('sms') && ' eller'}
            {available2FAMethods.includes('sms') && ' SMS'}
            {available2FAMethods.includes('authenticator') && ', eller bruk en backup-kode'}
          </p>
        </div>

        <Button
          type="submit"
          disabled={loading || twoFactorCode.replace(/\s/g, '').length < 6}
          className="w-full h-14 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white text-base font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all"
        >
          {loading ? (
            <div className="flex items-center justify-center space-x-2">
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              <span>Verifiserer...</span>
            </div>
          ) : (
            <>
              Bekreft
              <ArrowRight className="ml-2 w-5 h-5" />
            </>
          )}
        </Button>

        <button
          type="button"
          onClick={onReset}
          className="w-full text-slate-600 hover:text-slate-900 text-sm font-medium transition-colors py-2"
        >
          ← Bruk en annen konto
        </button>
      </form>
    </>
  );
}
